import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import { contactAPI } from '../../services/api';
import { 
  MapPin, Phone, Mail, Clock, Facebook, Twitter, Linkedin, Instagram, Youtube, Send, CheckCircle
} from 'lucide-react';

const ContactContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background.primary};
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: ${props => props.theme.colors.white};
  padding: 6rem 2rem 4rem 2rem;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['4xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  margin: 0 0 1rem 0;
`;

const HeroSubtitle = styled.p`
  font-size: ${props => props.theme.typography.fontSize.lg};
  margin: 0;
  opacity: 0.9;
`;

const Section = styled.section`
  flex: 1;
  width: 100%;
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const ContactForm = styled(Card)`
  padding: 2rem;
`;

const ContactInfo = styled(Card)`
  padding: 2rem;
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1.5rem 0;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: ${props => props.theme.typography.fontSize.base};
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const InfoSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const InfoItem = styled.div`
  padding: 0.75rem 0;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  
  a {
    color: ${props => props.theme.colors.text.secondary};
    text-decoration: none;
    transition: color 0.2s;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme.colors.primary}15;
  color: ${props => props.theme.colors.primary};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    flex-shrink: 0;
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1.5rem;
`;

const ContactPage = () => {
  const { theme } = useTheme();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getSettings();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching contact settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    try {
      await contactAPI.submitMessage(formData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      
      // Scroll to success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting message:', error);
      setError(error.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook':
        return <Facebook size={20} />;
      case 'twitter':
        return <Twitter size={20} />;
      case 'linkedin':
        return <Linkedin size={20} />;
      case 'instagram':
        return <Instagram size={20} />;
      case 'youtube':
        return <Youtube size={20} />;
      default:
        return null;
    }
  };

  return (
    <ContactContainer theme={theme}>
      <PublicHeader />
      
      <HeroSection theme={theme}>
        <HeroTitle>Get In Touch</HeroTitle>
        <HeroSubtitle>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</HeroSubtitle>
      </HeroSection>

      <Section>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: theme.colors.text.secondary }}>
            Loading contact information...
          </div>
        ) : (
          <Grid>
            {/* Contact Form */}
            <ContactForm theme={theme}>
              <FormTitle theme={theme}>Send Us a Message</FormTitle>
              
              {success && (
                <SuccessMessage>
                  <CheckCircle size={24} />
                  <div>
                    <strong>Message sent successfully!</strong><br />
                    We'll get back to you as soon as possible.
                  </div>
                </SuccessMessage>
              )}
              
              {error && (
                <ErrorMessage>{error}</ErrorMessage>
              )}
              
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Name *</Label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Your full name"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+251 XX XXX XXXX"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Subject *</Label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    placeholder="How can we help you?"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Message *</Label>
                  <TextArea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder="Tell us more about your inquiry..."
                    theme={theme}
                  />
                </FormGroup>

                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  disabled={submitting}
                  style={{ width: '100%' }}
                >
                  <Send size={20} style={{ marginRight: '0.5rem' }} />
                  {submitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </ContactForm>

            {/* Contact Information */}
            <ContactInfo theme={theme}>
              <FormTitle theme={theme}>Contact Information</FormTitle>
              
              {settings && (
                <>
                  {/* Address */}
                  {settings.address && (
                    <InfoSection>
                      <InfoTitle theme={theme}>
                        <MapPin size={20} />
                        Address
                      </InfoTitle>
                      <InfoItem theme={theme}>
                        {settings.address}<br />
                        {settings.po_box}<br />
                        {settings.landmark}
                      </InfoItem>
                    </InfoSection>
                  )}

                  {/* Phone Numbers */}
                  {settings.phone_numbers && settings.phone_numbers.length > 0 && (
                    <InfoSection>
                      <InfoTitle theme={theme}>
                        <Phone size={20} />
                        Phone
                      </InfoTitle>
                      {settings.phone_numbers.map((phone, index) => (
                        <InfoItem key={index} theme={theme}>
                          <a href={`tel:${phone}`}>{phone}</a>
                        </InfoItem>
                      ))}
                    </InfoSection>
                  )}

                  {/* Emails */}
                  {settings.emails && settings.emails.length > 0 && (
                    <InfoSection>
                      <InfoTitle theme={theme}>
                        <Mail size={20} />
                        Email
                      </InfoTitle>
                      {settings.emails.map((email, index) => (
                        <InfoItem key={index} theme={theme}>
                          <a href={`mailto:${email}`}>{email}</a>
                        </InfoItem>
                      ))}
                    </InfoSection>
                  )}

                  {/* Office Hours */}
                  {settings.office_hours && (
                    <InfoSection>
                      <InfoTitle theme={theme}>
                        <Clock size={20} />
                        Office Hours
                      </InfoTitle>
                      <InfoItem theme={theme}>{settings.office_hours}</InfoItem>
                    </InfoSection>
                  )}

                  {/* Social Media */}
                  {settings.social_media && Object.keys(settings.social_media).length > 0 && (
                    <InfoSection>
                      <InfoTitle theme={theme}>Follow Us</InfoTitle>
                      <SocialLinks>
                        {Object.entries(settings.social_media).map(([platform, url]) => url && (
                          <SocialLink
                            key={platform}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            theme={theme}
                            title={platform}
                          >
                            {getSocialIcon(platform)}
                          </SocialLink>
                        ))}
                      </SocialLinks>
                    </InfoSection>
                  )}
                  
                  {/* Additional Info */}
                  {settings.additional_info && (
                    <InfoSection>
                      <InfoItem theme={theme} style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>
                        {settings.additional_info}
                      </InfoItem>
                    </InfoSection>
                  )}
                </>
              )}
            </ContactInfo>
          </Grid>
        )}
      </Section>

      <PublicFooter />
    </ContactContainer>
  );
};

export default ContactPage;
